import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../contexts/user-context/user-context";
import { API_ENDPOINT } from "../configurations/global.config";
import axios from "axios";
import { useSnackBar } from "../contexts/snackbar";
import { IRole } from "../interfaces/common/roles";
import { RoleInput } from "../components/delivery-company-components/settings/permission-groups/role-modal";

interface UseRolesOptions {
  autoFetch?: boolean;
}

export const useRoles = ({ autoFetch = true }: UseRolesOptions = {}) => {
  const { config, user } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [roles, setRoles] = useState<IRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      const response = await axios.get<IRole[]>(
        `${API_ENDPOINT}/v1/roles/`,
        config
      );
      setRoles(response.data)
    } catch (error) {
      console.error("Failed to fetch roles", error);
      showMessage("Failed to fetch roles", "error");
    } finally {
      setIsLoading(false);
    }
  }, [config, user, showMessage]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && user) {
      fetchRoles();
    }
  }, [autoFetch, user, fetchRoles]);

  // Create a role
  const createRole = useCallback(
    async (role: RoleInput) => {
      let newRole: IRole | null = null;
      try {
        setIsLoading(true);
        const response = await axios.post(`${API_ENDPOINT}/v1/roles/`, role, config);
        newRole = response.data;
        setRoles(prev => [...prev, newRole!]);
      } catch (error) {
        console.error("Failed to create role", error);
        showMessage("Failed to create role", "error");
        return null;
      } finally {
        setIsLoading(false);
      }
      return newRole;
    },
    [config, showMessage]
  );

  // Update a role
  const updateRole = useCallback(
    async (id: number, updates: RoleInput) => {
      let updatedRole: IRole | null = null;
      try {
        setIsLoading(true);
        const response = await axios.patch(`${API_ENDPOINT}/v1/roles/${id}/`, updates, config);
        updatedRole = response.data;
        setRoles(prev =>
          prev.map(role => (role.id === id ? updatedRole! : role))
        );
      } catch (error) {
        console.error("Failed to update role", error);
        showMessage("Failed to update role", "error");
        return null;
      } finally {
        setIsLoading(false);
      }
      return updatedRole;
    },
    [config, showMessage]
  );

  // Delete a role
  const deleteRole = useCallback(
    async (id: number) => {
      let worked = false;
      try {
        setIsLoading(true);
        await axios.delete(`${API_ENDPOINT}/v1/roles/${id}/`, config);
        setRoles(prev => prev.filter(role => role.id !== id));
        worked = true;
      } catch (error) {
        console.error("Failed to delete role", error);
        throw error;
      } finally {
        setIsLoading(false);
      }

      return worked
    },
    [config, showMessage]
  );

  return {
    roles,
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,
    isLoading,
  };
};

export default useRoles;
